/**
 * Desktop-only click-to-zoom for product gallery images.
 *
 * On devices with a real pointer and hover support (i.e. not touchscreens,
 * regardless of viewport width), clicking a product image toggles an
 * in-place zoom that tracks the cursor as it moves; clicking again turns it
 * off. Touch/mobile devices never run this — matchMedia simply won't
 * match — so the existing click-to-zoom dialog keeps working there
 * unchanged.
 */

const HOVER_CAPABLE = window.matchMedia('(hover: hover) and (pointer: fine)');
const ZOOM_SCALE = 2.2;

/** @type {HTMLElement | null} */
let activeContainer = null;

/** @type {Set<string>} */
const highResImagesLoaded = new Set();

/**
 * @param {Element} container
 * @returns {boolean}
 */
function isZoomableImage(container) {
  return (
    container.classList.contains('product-media-container--image') && !container.closest('.dialog-zoomed-gallery')
  );
}

/**
 * Swaps in the full-resolution image so the zoom is sharp, instead of the
 * smaller responsive srcset candidate loaded for the gallery's normal
 * (unzoomed) display size, which looks soft once scaled up.
 * @param {HTMLElement} container
 */
function loadHighResolutionImage(container) {
  const image = container.querySelector('img.product-media__image');
  if (!(image instanceof HTMLImageElement)) return;

  const highResUrl = image.getAttribute('data_max_resolution');
  if (!highResUrl || highResImagesLoaded.has(highResUrl)) return;

  const newImage = new Image();
  newImage.className = image.className;
  newImage.alt = image.alt;
  newImage.setAttribute('data_max_resolution', highResUrl);

  newImage.onload = () => {
    image.replaceWith(newImage);
    highResImagesLoaded.add(highResUrl);
  };

  newImage.src = highResUrl;
}

/**
 * @param {HTMLElement} container
 * @param {number} clientX
 * @param {number} clientY
 */
function updateZoomPosition(container, clientX, clientY) {
  const rect = container.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;

  const x = ((clientX - rect.left) / rect.width) * 100;
  const y = ((clientY - rect.top) / rect.height) * 100;

  container.style.setProperty('--hover-zoom-x', `${Math.min(100, Math.max(0, x))}%`);
  container.style.setProperty('--hover-zoom-y', `${Math.min(100, Math.max(0, y))}%`);
}

function deactivate() {
  if (!activeContainer) return;
  activeContainer.classList.remove('is-hover-zooming');
  activeContainer = null;
}

/** @param {MouseEvent} event */
function handleClick(event) {
  if (!HOVER_CAPABLE.matches) return;

  const target = event.target;
  const container = target instanceof Element ? target.closest('.product-media-container') : null;
  if (!container || !isZoomableImage(container)) return;

  const htmlContainer = /** @type {HTMLElement} */ (container);

  if (activeContainer === htmlContainer) {
    deactivate();
    return;
  }

  deactivate();

  htmlContainer.style.setProperty('--hover-zoom-scale', `${ZOOM_SCALE}`);
  updateZoomPosition(htmlContainer, event.clientX, event.clientY);
  htmlContainer.classList.add('is-hover-zooming');
  activeContainer = htmlContainer;
  loadHighResolutionImage(htmlContainer);
}

/** @param {PointerEvent} event */
function handlePointerMove(event) {
  if (!activeContainer || event.pointerType !== 'mouse') return;
  updateZoomPosition(activeContainer, event.clientX, event.clientY);
}

/** @param {PointerEvent} event */
function handlePointerLeave(event) {
  const target = event.target;
  const container = target instanceof Element ? target.closest('.product-media-container') : null;
  if (container && container === activeContainer) {
    deactivate();
  }
}

document.addEventListener('click', handleClick, { capture: true });
document.addEventListener('pointermove', handlePointerMove, { passive: true });
// pointerleave doesn't bubble, but capture-phase delivery still reaches a
// document-level listener as the event travels down to its target.
document.addEventListener('pointerleave', handlePointerLeave, { capture: true, passive: true });
